import { useMemo, useState } from 'react';
import { UIMessages } from '../../useChatView';
import { useTranslate } from '@tolgee/react';
import { useSetConversationInputDraft } from 'hooks/business/conversationDraft';
import { SysRecallMsg } from '../MessageType/SysRecallMsg';
import { SysFaqQuestionMsg } from '../MessageType/SysFaqQuestionMsg';
import { SysUserEnterFromItemMsg } from '../MessageType/SysUserEnterFromItemMsg';
import { SysUserEnterFromOrderMsg } from '../MessageType/SysUserEnterFromOrderMsg';
import { SysReOpenConversationMsg } from '../MessageType/SysReOpenConversationMsg';
import { SysCloseConversationMsg } from '../MessageType/SysCloseConversationMsg';
import { SysForceAcquireControlMsg } from '../MessageType/SysForceAcquireControlMsg';
import { SysIgnoreBotSentLimitReachedMsg } from '../MessageType/SysIgnoreBotSentLimitReachedMsg';
import { SysProcessConversationMsg } from '../MessageType/SysProcessConversationMsg';
import { SysAutoCancelOrderMsg } from '../MessageType/SysAutoCancelOrderMsg';
import { SysRemindSolveMsg } from '../MessageType/SysRemindSolveMsg';
import { SysAutoMarkSalesStatusMsg } from '../MessageType/SysAutoMarkSalesStatusMsg';
import { SysManualMarkSalesStatusMsg } from '../MessageType/SysManualMarkSalesStatusMsg';
import { SysAsteriskStarMsg } from '../MessageType/SysAsteriskStarMsg';
import { SysInitConversationMsg } from '../MessageType/SysInitConversationMsg';
import { SysOutMsg } from '../MessageType/SysOutMsg';
import isNonsupportedMessageType from '../NonsupportedMessageV2/isNonsupportedMessageType';
import { MessageExtendComplex } from '../MessageExtend/MessageExtendComplex';
import NonsupportedMessageV2 from '../NonsupportedMessageV2';
import { TextMsg } from '../MessageType/TextMsg';
import { StickerMsg } from '../MessageType/StickerMsg';
import { ImageMsg } from '../MessageType/ImageMsg';
import { VideoMsg } from '../MessageType/VideoMsg';
import { ItemMsg } from '../MessageType/ItemMsg';
import { FollowMsg } from '../MessageType/FollowMsg';
import { OrderMsg } from '../MessageType/OrderMsg';
import { VoucherMsg } from '../MessageType/VoucherMsg';
import { RefundMsg } from '../MessageType/RefundMsg';
import { TextVideoMsg } from '../MessageType/TextVideoMsg';
import { SystemMsg } from '../MessageType/SystemMsg';
import { OfferMsg } from '../MessageType/OfferMsg';
import LazadaOfferMessage from '../LazadaOfferMessage';
import { TemplateMsg } from '../MessageType/TemplateMsg';
import { BundleMessageMsg } from '../MessageType/BundleMessageMsg';
import { ImageWithTextMsg } from '../MessageType/ImageWithTextMsg';
import { AttachmentMsg } from '../MessageType/AttachmentMsg';
import { KwFaqMsg } from '../MessageType/KwFaqMsg';
import { KwOrderRemindMsg } from '../MessageType/KwOrderRemindMsg';
import { KwPaymentRemindMsg } from '../MessageType/KwPaymentRemindMsg';
import { KwEvaluationMsg } from '../MessageType/KwEvaluationMsg';
import ReturnRefundCardMsg from '../MessageType/ReturnRefundCardMsg';
import FaqUnsupported from '../MessageType/FaqUnsupported';
import NewFaq from '../MessageType/NewFaq';
import FaqCategoryChoice from '../MessageType/FaqCategoryChoice';
import FaqLiveagentPrompt from '../MessageType/FaqLiveagentPrompt';
import FaqFeedbackPrompt from '../MessageType/FaqFeedbackPrompt';
import FaqFeedback from '../MessageType/FaqFeedback';
import FaqQuestionList from '../MessageType/FaqQuestionList';
import FaqQuestion from '../MessageType/FaqQuestion';
import { NonsupportMessage, SystemMessage } from 'components/Dialogue';
import { FileMsg } from '../MessageType/FileMsg';
import FoldMsg from '../MessageType/FoldMsg';
import TtsLogisticsCardMsg from '../MessageType/TtsLogisticsCardMsg';
import FaqShopeeRichText from '../MessageType/FaqShopeeRichText';
import FaqBotRequestText from '../MessageType/FaqBotRequestText';
import FacebookImageMsg from '../MessageType/FacebookImageMsg';
import VariationCardMsg from '../MessageType/VariationCardMsg';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import OtherCardMessage from '../OtherCardMessage/OtherCardMessage';
import { SysCsatMsg } from '../MessageType/SysCsatMsg';

function useMessageRender(
  conversation: Conversation.Conversation,
  nouseMessageList?: Conversation.NouseMessage[][],
) {
  const { t } = useTranslate();

  const setInput = useSetConversationInputDraft();

  const [nouseExpandMap, setNouseExpandMap] = useState<Record<number, boolean>>({});

  const messageRender = useMemo(() => {
    const renderer = (msg: UIMessages) => {
      if (conversation === undefined) {
        return null;
      }
      const { type, raw } = msg;
      const { msgId, messageType, status: rawStatus } = raw;

      // 撤回消息
      if (rawStatus === 'RECALL') {
        return <SysRecallMsg msg={msg} conversation={conversation} raw={raw} onClick={setInput} />;
      }

      const nouseTargetIndex = nouseMessageList?.findIndex((_) => _.some((_) => _.msgId === msgId));

      if (nouseTargetIndex !== undefined && nouseTargetIndex >= 0 && nouseMessageList) {
        if (!nouseExpandMap[nouseTargetIndex]) {
          const nouseTarget = nouseMessageList[nouseTargetIndex];
          if (nouseTarget.length > 0 && msgId === nouseTarget[nouseTarget.length - 1].msgId) {
            return (
              <FoldMsg
                raw={raw}
                msg={msg}
                conversation={conversation}
                nouseMessageList={nouseTarget}
                onExpand={() =>
                  setNouseExpandMap({
                    ...nouseExpandMap,
                    [nouseTargetIndex]: true,
                  })
                }
              />
            );
          } else {
            return null;
          }
        }
      }

      switch (messageType) {
        case 'SYS_FAQ_QUESTION': {
          return <SysFaqQuestionMsg raw={raw} conversation={conversation} />;
        }
        case 'NOTIFICATION': {
          return <SystemMessage>{raw?.notificationContent?.text}</SystemMessage>;
        }
        case 'SYS_USER_ENTER_FROM_ITEM': {
          return <SysUserEnterFromItemMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_USER_ENTER_FROM_ORDER': {
          return <SysUserEnterFromOrderMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_USER_ENTER_FROM_TRANSFER': {
          return <SystemMessage>{raw.sysUserEnterFromTransferContent?.content}</SystemMessage>;
        }
        case 'SYS_ALLOCATED_SERVICE': {
          return <SystemMessage>{raw.sysAllocatedServiceContent?.content}</SystemMessage>;
        }
        case 'NONSUPPORT': {
          if (isTiKTokUpperCase(raw.channel)) {
            return raw.nonsupportContent?.content ? (
              <MessageExtendComplex
                raw={raw}
                msg={msg}
                conversation={conversation}
                content={<OtherCardMessage content={raw.nonsupportContent?.content} />}
              />
            ) : null;
          }
          break;
        }
        case 'SYS_RE_OPEN_CONVERSATION': {
          return <SysReOpenConversationMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_NEG_RATE': {
          return <SystemMessage>{t('sys_neg_rate_message_content')}</SystemMessage>;
        }
        case 'SYS_CANCEL_ORDER': {
          return <SystemMessage>{t('sys_cancel_order_message_content')}</SystemMessage>;
        }
        case 'SYS_CLOSE_CONVERSATION': {
          return <SysCloseConversationMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_FORCE_ACQUIRE_CONTROL': {
          return <SysForceAcquireControlMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_IGNORE_BOT_SENT_LIMIT_REACHED': {
          return <SysIgnoreBotSentLimitReachedMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_NEG_RATE_PAUSE_AI_EXPIRE': {
          return (
            <SystemMessage>
              {t('ViewsChat.中/差评挽回 48 小时暂停结束，系统已自动恢复AI回复')}
            </SystemMessage>
          );
        }
        case 'SYS_NEG_RATE_PAUSE_AI_PROCESS': {
          return (
            <SystemMessage>{t('ViewsChat.买家中/差评已处理，系统已自动恢复AI回复')}</SystemMessage>
          );
        }
        case 'SYS_NEG_RATE_PAUSE_AI_START': {
          return (
            <SystemMessage>
              {t(
                'ViewsChat.该买家发起了中/差评，为保障人工客服跟进，当前会话机器人将在 48 小时内暂停回复',
              )}
            </SystemMessage>
          );
        }
        case 'SYS_PROCESS_CONVERSATION': {
          return <SysProcessConversationMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_CSAT': {
          return <SysCsatMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_AUTO_CANCEL_ORDER': {
          return <SysAutoCancelOrderMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_REMIND_SOLVE': {
          return <SysRemindSolveMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_AUTO_MARK_SALES_STATUS': {
          return <SysAutoMarkSalesStatusMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_MANUAL_MARK_SALES_STATUS': {
          return <SysManualMarkSalesStatusMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_ASTERISK_STAR': {
          return <SysAsteriskStarMsg raw={raw} conversation={conversation} />;
        }
        case 'SYS_WA_REJECT': {
          return <SystemMessage>{t('messageType.SYS_WA_REJECT')}</SystemMessage>;
        }
        case 'SYS_INIT_CONVERSATION': {
          return <SysInitConversationMsg raw={raw} conversation={conversation} msg={msg} />;
        }
        case 'SYS_OUT': {
          return <SysOutMsg raw={raw} conversation={conversation} />;
        }
        // 会话分流
        case 'SYS_ASSIGN_CONTEXT':
        case 'SYS_TRANSFER': {
          return (
            <SystemMsg
              raw={raw}
              msg={msg}
              conversation={conversation}
              skipHistoryDivider={false}
              showDistributeButton
            />
          );
        }
        case 'SHOPEE_RETURN_REFUND_CARD': {
          return (
            <MessageExtendComplex
              raw={raw}
              msg={msg}
              conversation={conversation}
              content={<NonsupportedMessageV2 dataSource={raw} />}
            />
          );
          // return <ShopeeReturnRefundCardMsg raw={raw} msg={msg} conversation={conversation} />;
        }
        case 'FAQ_UNSUPPORTED': {
          return <FaqUnsupported raw={raw} msg={msg} conversation={conversation} />;
        }
        case 'NEW_FAQ': {
          return <NewFaq raw={raw} msg={msg} conversation={conversation} />;
        }
        case 'FAQ_CATEGORY_CHOICE': {
          return <FaqCategoryChoice raw={raw} msg={msg} conversation={conversation} />;
        }
        case 'FAQ_LIVEAGENT_PROMPT': {
          return <FaqLiveagentPrompt raw={raw} msg={msg} conversation={conversation} />;
        }
        case 'FAQ_FEEDBACK_PROMPT': {
          return <FaqFeedbackPrompt raw={raw} msg={msg} conversation={conversation} />;
        }
        case 'FAQ_FEEDBACK': {
          return <FaqFeedback raw={raw} msg={msg} conversation={conversation} />;
        }
        case 'FAQ_QUESTION_LIST': {
          return <FaqQuestionList raw={raw} msg={msg} conversation={conversation} />;
        }
        case 'SHOPEE_RICH_TEXT': {
          return <FaqShopeeRichText raw={raw} msg={msg} conversation={conversation} />;
        }
        case 'FAQ_BOT_REQUEST_TEXT': {
          return <FaqBotRequestText raw={raw} msg={msg} conversation={conversation} />;
        }
        case 'FAQ_QUESTION': {
          return <FaqQuestion raw={raw} msg={msg} conversation={conversation} />;
        }
        default: {
          break;
        }
      }

      if (isNonsupportedMessageType(raw)) {
        return (
          <MessageExtendComplex
            raw={raw}
            msg={msg}
            conversation={conversation}
            content={<NonsupportedMessageV2 dataSource={raw} />}
          />
        );
      } else {
        switch (type) {
          case 'text': {
            return <TextMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'sticker': {
            return <StickerMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'image': {
            return <ImageMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'video': {
            return <VideoMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'order': {
            return <OrderMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'item': {
            return <ItemMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'follow': {
            return <FollowMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'voucher': {
            return <VoucherMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'refund': {
            return <RefundMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'textVideo': {
            return <TextVideoMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'system': {
            return (
              <SystemMsg
                raw={raw}
                msg={msg}
                conversation={conversation}
                skipHistoryDivider={true}
              />
            );
          }
          case 'offer': {
            return <OfferMsg raw={raw} conversation={conversation} msg={msg} />;
          }
          case 'lazadaOffer': {
            return (
              <MessageExtendComplex
                raw={raw}
                msg={msg}
                conversation={conversation}
                content={<LazadaOfferMessage content={msg.content as any} />}
              />
            );
          }
          case 'template': {
            return <TemplateMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'bundleMessage': {
            return <BundleMessageMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'imageWithText': {
            return <ImageWithTextMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'attachment': {
            return <AttachmentMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'kwNotice': {
            return <SystemMessage>{raw.kwNoticeContent?.text}</SystemMessage>;
          }
          case 'kwPrologue': {
            return <SystemMessage>{raw.kwPrologueContent?.text}</SystemMessage>;
          }
          case 'kwFaq': {
            return <KwFaqMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'kwOrderCard': {
            return <KwOrderRemindMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'kwPaymentCard': {
            return <KwPaymentRemindMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'kwEvaluation': {
            return <KwEvaluationMsg raw={raw} msg={msg} conversation={conversation} />;
          }
          case 'returnRefundCard': {
            return <ReturnRefundCardMsg raw={raw} msg={msg as any} conversation={conversation} />;
          }
          case 'facebook': {
            const { fbMsgContent: { attachments: { data = [] } = {} } = {} } = raw;
            const [{ fileUrl, name } = {}] = data;
            const imgData = data.filter(({ imageData }) => imageData?.url);
            if (imgData.length > 1) {
              return <FacebookImageMsg raw={raw} msg={msg} conversation={conversation} />;
            } else if (fileUrl || name) {
              return <FileMsg raw={raw} msg={msg} conversation={conversation} />;
            } else {
              return (
                <MessageExtendComplex
                  raw={raw}
                  msg={msg}
                  conversation={conversation}
                  content={<NonsupportMessage />}
                />
              );
            }
          }
          case 'ttsLogisticsCard': {
            return <TtsLogisticsCardMsg raw={raw} msg={msg as any} conversation={conversation} />;
          }
          case 'variationCard': {
            return <VariationCardMsg raw={raw} msg={msg as any} conversation={conversation} />;
          }
          default: {
            if (
              (conversation?.channel === 'WA_BIZ' && raw.msgFromType === 'BUYER') ||
              conversation?.channel === 'FACEBOOK'
            ) {
              return (
                <MessageExtendComplex
                  raw={raw}
                  msg={msg}
                  conversation={conversation}
                  content={<NonsupportMessage />}
                />
              );
            } else {
              return null;
            }
          }
        }
      }
    };
    return renderer;
  }, [conversation, t, setInput, nouseMessageList, nouseExpandMap]);

  return {
    t,
    setInput,
    messageRender,
    nouseExpandMap,
    setNouseExpandMap,
  };
}

export default useMessageRender;
